import React, { useCallback } from 'react';
import { getIn } from 'formik';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import classnames from 'classnames';

import {
  DANGEROUS_ATTACHMENT_NAME_REGEX,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENT_FILE_NAME_LENGTH,
} from '@common/constants/sharedConstants';

import ErrorMessage from '../select/errorMessage.component';
import SingleAttachment from './singleAttachment.component';

import { FormikPropTypes } from '../propTypes';

const MAX_ATTACHMENT_MB = MAX_ATTACHMENT_BYTES / (1024 * 1024);

const AttachmentErrors = {
  CANNOT_UPLOAD: 'Cannot upload this file',
  FILE_TOO_LARGE: `File exceeds the ${MAX_ATTACHMENT_MB} MB limit`,
  FILE_NAME_TOO_LONG: `File name exceeds ${MAX_ATTACHMENT_FILE_NAME_LENGTH} characters`,
  DANGEROUS_FILE_NAME: 'File name contains a blocked extension (e.g. .php, .html, .exe)',
  FILE_TYPE_NOT_ALLOWED: acceptTypes =>
    `File type not allowed. Only ${acceptTypes.join(', ')} files are accepted`,
  FILE_READING_ABORTED: 'File reading was aborted',
  FILE_READING_FAILED: 'File reading has failed',
};

const validateAttachmentFile = (file, acceptTypes) => {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return AttachmentErrors.FILE_TOO_LARGE;
  }
  if (file.name.length > MAX_ATTACHMENT_FILE_NAME_LENGTH) {
    return AttachmentErrors.FILE_NAME_TOO_LONG;
  }
  if (DANGEROUS_ATTACHMENT_NAME_REGEX.test(file.name)) {
    return AttachmentErrors.DANGEROUS_FILE_NAME;
  }
  if (acceptTypes && acceptTypes.length) {
    const extension = file.name.split('.').pop().toLowerCase();
    const normalizedAcceptTypes = acceptTypes.map(type =>
      type.replace(/^\./, '').toLowerCase()
    );
    if (!normalizedAcceptTypes.includes(extension)) {
      return AttachmentErrors.FILE_TYPE_NOT_ALLOWED(acceptTypes);
    }
  }
  return null;
};

const AttachmentsFormik = ({
  name,
  label = '',
  disabled = false,
  formik,
  acceptTypes,
  multiple = false,
  withThumbnails = false,
  className = '',
}) => {
  const value = getIn(formik.values, name);

  const setUploadError = useCallback(message => {
    formik.setFieldError(name, message);
    formik.setFieldTouched(name, true, false);
  }, [formik, name]);

  const addFileToForm = useCallback(file => {
    if (multiple && value && value.find(({ name }) => file.name === name)) {
      return;
    }
    const newValue = multiple && value ? [...value, file] : file;

    formik.setFieldValue(name, newValue);
  }, [value, name, formik, multiple]);

  const dropHandler = useCallback(files => {
    const file = files[0];

    if (!file) {
      setUploadError(AttachmentErrors.CANNOT_UPLOAD);
      return;
    }

    const validationError = validateAttachmentFile(file, acceptTypes);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const fileAsDataUrl = reader.result;
      const fileExtension = file.name.split('.').pop();

      addFileToForm({
        name: file.name,
        type: fileExtension,
        image: fileAsDataUrl.split('base64,')[1],
        url: fileAsDataUrl,
      });
    };
    reader.onabort = () => setUploadError(AttachmentErrors.FILE_READING_ABORTED);
    reader.onerror = () => setUploadError(AttachmentErrors.FILE_READING_FAILED);

    reader.readAsDataURL(file);
  }, [acceptTypes, addFileToForm, setUploadError]);

  const removeHandler = useCallback(fileToRemove => {
    const newValue = multiple
      ? value.filter(selectedFile => selectedFile !== fileToRemove)
      : null;

    formik.setFieldValue(name, newValue);
  }, [name, formik, multiple, value]);

  const error = getIn(formik.errors, name);
  const touched = getIn(formik.touched, name);

  const shouldUseErrorStyles = touched && error;

  return (
    <div className={`dropzone-formik ${className}`} name={name}>
      {label ? <label className="a-form-label">{label}</label> : null}
      <div className="a-upload">
        <div className="a-upload-box single-upload">
          <div className="a-upload-drag-zone">
            <Dropzone
              onDrop={dropHandler}
              disabled={disabled}
              accept={acceptTypes}
              multiple={multiple}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps({
                    className: classnames('upload-drop-small', {
                      'upload-drop-error': shouldUseErrorStyles,
                    }),
                  })}
                >
                  <input {...getInputProps({ className: 'upload-input' })} />
                  <span className="upload-drop-span">Drag and Drop or</span>
                  <span className="upload-browse-span">
                    &nbsp;choose your {`file${multiple ? 's' : ''}`}
                  </span>.

                  { acceptTypes && (
                    <>
                      <br />
                      <span className="upload-drop-span">
                        Only {acceptTypes.join(', ')} files will be accepted.
                      </span>
                    </>
                  )}

                  <br />
                  <span className="upload-drop-span">Maximum upload size is 10 MB (10240K).</span>
                </div>)
              }
            </Dropzone>

          </div>
          <div className="a-upload-file-list">
            {multiple && value
              ? value.map(value =>
                <SingleAttachment
                  file={value}
                  onRemove={removeHandler}
                  withThumbnail={withThumbnails}
                  key={value.name}
                />)
              : value && <SingleAttachment
                file={value}
                onRemove={removeHandler}
                withThumbnail={withThumbnails}
              />
            }
          </div>
        </div>
      </div>
      <div className="invalid-feedback">
        <ErrorMessage name={name} formik={formik} />
      </div>
    </div>
  );
};

AttachmentsFormik.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  formik: FormikPropTypes.isRequired,
  acceptTypes: PropTypes.arrayOf(PropTypes.string.isRequired),
  multiple: PropTypes.bool,
  withThumbnails: PropTypes.bool,
  className: PropTypes.string,
};

export default AttachmentsFormik;
